// Feature: lead-commercial-auto-risk-profile
//
// Shared, pure keying helpers for the Commercial Auto (`commAuto.*`) module thunks.
//
// Every Commercial Auto list-fetch and save is keyed by ONE of two scopes:
//   - Lead scope:  keyed by `lead_id`, with `customer_policy_id` omitted entirely.
//   - Policy scope: the legacy customer-policy behavior, keyed by `customer_policy_id`.
//
// The keying rule is uniform across every module (Driver, Vehicle, Garage Plates,
// Coverage/Schedule A–D, Risk Profile, Applications, Marketing): if `leadId` is a
// non-empty string, key by `lead_id`; otherwise fall back to the exact legacy string /
// body verbatim so the customer-policy request shape is byte-for-byte unchanged
// (non-regression, Requirement 16.1/16.3).
//
// These functions perform no I/O and hold no state, so they can be exhaustively
// property-tested (design Properties 5 and 6).

/**
 * Narrowing predicate for lead scope: `leadId` is treated as present only when it is a
 * non-empty, non-whitespace-only string. Anything else (`undefined`, `null`, `""`, or a
 * whitespace-only string) resolves to the legacy customer-policy scope.
 */
export const hasLeadScope = (leadId?: string | null): leadId is string =>
  typeof leadId === "string" && leadId.trim().length > 0;

/**
 * Builds the scope query-string fragment for a GET list/fetch request, WITHOUT a leading
 * `?` or `&` so callers can splice it into an existing URL the same way they do today.
 *
 * - Lead scope:  `lead_id=${leadId}` (no `customer_policy_id`).
 * - Policy scope: `customer_policy_id=${customerPolicyId}` — copied verbatim from the
 *   current thunks to guarantee non-regression.
 *
 * The `leadId` is URL-encoded in lead scope; the legacy `customer_policy_id` branch is
 * emitted exactly as the existing thunks build it (raw interpolation) so the produced
 * string is identical to today's behavior.
 *
 * @example
 *   `/policy/get-policy-commauto-driver?${buildListKey({ customerPolicyId })}`
 *   `/policy/get-policy-commauto-driver?${buildListKey({ leadId })}`
 */
export const buildListKey = (params: {
  customerPolicyId?: number;
  leadId?: string | null;
}): string => {
  const { customerPolicyId, leadId } = params;
  if (hasLeadScope(leadId)) {
    return `lead_id=${encodeURIComponent(leadId)}`;
  }
  // Legacy customer-policy branch — verbatim shape used by every current thunk.
  return `customer_policy_id=${customerPolicyId}`;
};

/**
 * Builds the POST body for a save request by merging the scope key into the supplied
 * record data.
 *
 * - Lead scope:  `{ ...data, lead_id: leadId, customer_policy_id: null }` — the record is
 *   re-keyed to the lead and any inbound `customer_policy_id` is explicitly nulled so it
 *   is never persisted against a policy.
 * - Policy scope: `{ ...data }` verbatim — the record keeps whatever `customer_policy_id`
 *   it already carries, exactly as the current save thunks send it (non-regression).
 *
 * `incremental_number` (and every other field on `data`) is preserved untouched in both
 * scopes, so fetch-one/save on an existing record keeps its identifier.
 */
export const buildBodyKey = <T extends Record<string, unknown>>(
  data: T,
  leadId?: string | null
): T & { lead_id?: string; customer_policy_id?: number | null } => {
  if (hasLeadScope(leadId)) {
    return {
      ...data,
      lead_id: leadId,
      customer_policy_id: null,
    };
  }
  // Legacy customer-policy branch — body sent verbatim, carrying customer_policy_id.
  return { ...data };
};
